import * as React from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Card from '@mui/material/Card';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import BoltIcon from '@mui/icons-material/Bolt';
import ScienceIcon from '@mui/icons-material/Science';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import AirIcon from '@mui/icons-material/Air';
import CompressIcon from '@mui/icons-material/Compress';
import LightModeIcon from '@mui/icons-material/LightMode';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import { SENSOR_TYPES } from '../context/SensorConfigContext';
import { useGrow } from '../context/GrowContext';
import ConfigurableSensorCard from './ConfigurableSensorCard';

// Icon mapping
const ICON_MAP = {
  WaterDrop: WaterDropIcon,
  Bolt: BoltIcon,
  Science: ScienceIcon,
  DeviceThermostat: DeviceThermostatIcon,
  Thermostat: ThermostatIcon,
  Air: AirIcon,
  Compress: CompressIcon,
  LightMode: LightModeIcon,
  WbSunny: WbSunnyIcon,
};

function ConfigurableSensorSection({ section, growId }) {
  const { grows, updateGrowDashboardConfig } = useGrow();
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [editedTitle, setEditedTitle] = React.useState(section.title);
  const [addMenuAnchor, setAddMenuAnchor] = React.useState(null);

  // Get current grow's sections
  const currentGrow = grows.find(g => g.id === growId);
  const sections = currentGrow?.dashboardConfig?.sections || [];
  const sectionIndex = sections.findIndex(s => s.id === section.id);
  const isFirst = sectionIndex === 0;
  const isLast = sectionIndex === sections.length - 1;

  // Update section in grow's config
  const updateSection = (sectionId, updates) => {
    const updatedSections = sections.map(s =>
      s.id === sectionId ? { ...s, ...updates } : s
    );
    updateGrowDashboardConfig(growId, { sections: updatedSections });
  };

  // Delete section from grow's config
  const deleteSection = (sectionId) => {
    const updatedSections = sections.filter(s => s.id !== sectionId);
    updateGrowDashboardConfig(growId, { sections: updatedSections });
  };

  // Move section up or down
  const moveSection = (sectionId, direction) => {
    const idx = sections.findIndex(s => s.id === sectionId);
    if (idx === -1) return;

    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= sections.length) return;

    const updatedSections = [...sections];
    [updatedSections[idx], updatedSections[newIdx]] = [updatedSections[newIdx], updatedSections[idx]];
    updateGrowDashboardConfig(growId, { sections: updatedSections });
  };

  // Add sensor to section
  const addSensor = (sensorType) => {
    const sensorConfig = SENSOR_TYPES[sensorType];
    if (!sensorConfig) return;

    const newSensor = {
      id: `sensor-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: sensorType,
      size: 'small',
    };

    const updatedSensors = [...(section.sensors || []), newSensor];
    updateSection(section.id, { sensors: updatedSensors });
  };

  // Sync edited title when section changes
  React.useEffect(() => {
    setEditedTitle(section.title);
  }, [section.title]);

  const handleTitleChange = (e) => {
    setEditedTitle(e.target.value);
  };

  const handleTitleBlur = () => {
    if (editedTitle.trim() && editedTitle !== section.title) {
      updateSection(section.id, { title: editedTitle.trim() });
    }
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    } else if (e.key === 'Escape') {
      setEditedTitle(section.title);
    }
  };

  const handleAddClick = (e) => {
    setAddMenuAnchor(e.currentTarget);
  };

  const handleAddClose = () => {
    setAddMenuAnchor(null);
  };

  const handleAddSensor = (sensorType) => {
    addSensor(sensorType);
    handleAddClose();
  };

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  // Get grid size based on sensor size
  const getGridSize = (size) => {
    if (size === 'big') {
      return { xs: 12, sm: 6, md: 4 };
    }
    return { xs: 12, sm: 6, md: 4, lg: 2.4 };
  };

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      {/* Section Header */}
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        {/* Title - editable in edit mode */}
        {isEditMode ? (
          <TextField
            value={editedTitle}
            onChange={handleTitleChange}
            onBlur={handleTitleBlur}
            onKeyDown={handleTitleKeyDown}
            size="small"
            autoFocus
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '1.1rem',
                fontWeight: 600,
                fontFamily: 'Inter, sans-serif',
                py: 0.5,
              },
            }}
          />
        ) : (
          <Typography
            component="h2"
            variant="h6"
            sx={{ fontFamily: 'Inter, sans-serif', fontSize: '1.1rem' }}
          >
            {section.title}
          </Typography>
        )}

        <Box sx={{ flex: 1 }} />

        {/* Sliding edit controls - appear from right */}
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            maxWidth: isEditMode ? 200 : 0,
            opacity: isEditMode ? 1 : 0,
          }}
        >
          <IconButton
            size="small"
            onClick={() => moveSection(section.id, 'up')}
            disabled={isFirst}
            sx={{ color: 'text.secondary' }}
          >
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => moveSection(section.id, 'down')}
            disabled={isLast}
            sx={{ color: 'text.secondary' }}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleAddClick}
            sx={{ color: 'primary.main' }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => deleteSection(section.id)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* Single Edit button */}
        <Button
          size="small"
          variant={isEditMode ? 'contained' : 'outlined'}
          startIcon={<EditIcon sx={{ fontSize: 14 }} />}
          onClick={toggleEditMode}
          sx={{
            textTransform: 'none',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.75rem',
            py: 0.5,
            ml: 1,
          }}
        >
          {isEditMode ? 'Done' : 'Edit'}
        </Button>
      </Stack>

      {/* Sensors Grid */}
      {section.sensors && section.sensors.length > 0 ? (
        <Grid container spacing={2}>
          {section.sensors.map((sensor) => (
            <Grid key={sensor.id} size={getGridSize(sensor.size)}>
              <ConfigurableSensorCard
                sectionId={section.id}
                sensor={sensor}
                growId={growId}
                isEditMode={isEditMode}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif', mb: 1 }}
          >
            No sensors in this section
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
          >
            Add Sensor
          </Button>
        </Box>
      )}

      {/* Add Sensor Menu */}
      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={handleAddClose}
        PaperProps={{
          sx: { maxHeight: 400 },
        }}
      >
        <Typography variant="caption" sx={{ px: 2, py: 1, color: 'text.secondary', display: 'block' }}>
          Select Sensor Type
        </Typography>
        <Divider />
        {Object.entries(SENSOR_TYPES).map(([key, config]) => {
          const IconComponent = ICON_MAP[config.icon] || WaterDropIcon;
          return (
            <MenuItem key={key} onClick={() => handleAddSensor(key)}>
              <ListItemIcon>
                <IconComponent fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={config.label} secondary={`${config.min}-${config.max} ${config.unit}`} />
            </MenuItem>
          );
        })}
      </Menu>
    </Card>
  );
}

ConfigurableSensorSection.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    sensors: PropTypes.array,
  }).isRequired,
  growId: PropTypes.string.isRequired,
};

export default ConfigurableSensorSection;
